import { useTranslation } from "react-i18next";
import { Button } from "react-native-paper";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useAppTheme } from "../../../../useAppTheme";
import { useDevice } from "../../../../useDevice";
import Box from "../../../Box";
import TooltipWrapper from "../../../TooltipWrapper";
import { getStudySessionBottomBarHeight } from "../../constants";

type Props = {
	nextDisabled?: boolean;
	nextButtonTooltip?: string;
	nextButtonState: "finish" | "skip" | "next";
	handleNext: () => void;
	inReviewMode?: boolean;
	showPreviousButton?: boolean;
	handlePrevious?: () => void;
	exitConfig?: { onPress: () => void; label: string };
};

const PteSummativeBottomAppbar = ({
	nextDisabled,
	nextButtonTooltip,
	nextButtonState,
	handleNext,
	inReviewMode,
	showPreviousButton,
	handlePrevious,
	exitConfig,
}: Props) => {
	const theme = useAppTheme();
	const { t } = useTranslation();
	const insets = useSafeAreaInsets();
	const { isMobile } = useDevice();

	const nextLabel =
		nextButtonState === "finish"
			? t("session.navigation.finish")
			: t("testDriver.pteSummativeBottomAppbar.next");
	const nextButton = (
		<Button
			mode={nextButtonState === "skip" ? "outlined" : "contained"}
			icon="chevron-right"
			disabled={nextDisabled}
			onPress={handleNext}
			contentStyle={{ flexDirection: "row-reverse" }}
			accessibilityHint={nextButtonTooltip}
		>
			{nextLabel}
		</Button>
	);

	return (
		<Box
			backgroundColor={theme.colors.onPrimary}
			style={{
				borderTopWidth: 1,
				borderTopColor: theme.palette.divider,
			}}
			height={getStudySessionBottomBarHeight({
				safeAreaBottom: insets.bottom,
				isMobile,
				isSummativeTest: true,
				inReviewMode,
			})}
			flexDirection="row"
			alignItems="center"
			justifyContent={exitConfig ? "space-between" : "flex-end"}
			paddingHorizontal={24}
			paddingVertical={16}
		>
			{exitConfig && (
				<Button
					mode="outlined"
					icon="logout"
					textColor={theme.colors.onSurface}
					style={{ borderColor: theme.palette.divider }}
					onPress={exitConfig.onPress}
					accessibilityLabel={exitConfig.label}
				>
					{exitConfig.label}
				</Button>
			)}
			<Box flexDirection="row" alignItems="center" gap={16}>
				{showPreviousButton && handlePrevious && (
					<Button
						mode="outlined"
						icon="chevron-left"
						textColor={theme.colors.onSurface}
						style={{ borderColor: theme.palette.divider }}
						onPress={handlePrevious}
						accessible
						accessibilityLabel="Previous question"
					>
						{t("session.navigation.previous")}
					</Button>
				)}
				{nextButtonTooltip ? (
					<TooltipWrapper title={nextButtonTooltip}>
						{nextButton}
					</TooltipWrapper>
				) : (
					nextButton
				)}
			</Box>
		</Box>
	);
};

export default PteSummativeBottomAppbar;
